import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy } from 'lodash'
import DtoSimpleStation from 'home/dto/DtoSimpleStation'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_PLUVIO_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import PluviometryThunk from 'pluviometry/actions/PluviometryThunk'
import useAccountSetting from '../../utils/customHook/useAccountSetting'
import StationAction from '../../station/actions/StationAction'
import MapSituationPie from '../../station/components/mapSituation/MapSituationPie'
import { Card, CardContent, Grid } from '@mui/material'
import { NewTable } from '../../components/datatable/NewTable'
import { CardTitle } from '../../components/card/NewCard'
import { darkBlue } from '../../utils/constants/ColorTheme'
import InsertChartIcon from '@mui/icons-material/InsertChart'
import ProgressBar from '../../components/progress/ProgressBar'

const pluvioStationType = 2

const PluviometryCard = () => {
    const {
        userBookmarks,
        pluviometers,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        pluviometersStatus,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        pluviometers: store.PluviometryReducer.pluviometers,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        pluviometersStatus: store.DataManagerReducer.pluviometry.pluviometersStatus,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, pluviometersStatus])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(PluviometryThunk.fetchPluviometers())
    }, [])

    const mapSituationWidgetObj = useAccountSetting('MAP_SITUATION_DASHBOARD_WIDGET', setting => JSON.parse(setting ?? '{}'))
    const [mapSituationStats, setMapSituationStats] = useState([])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.pluviometry), [userBookmarks])

    useEffect(() => {
        const pluvioMapIds = Object.keys(mapSituationWidgetObj).filter(key => key.startsWith(STATION_TYPE_NAME.pluviometry) && !!mapSituationWidgetObj[key]).map(k => parseInt(k.replace('pluviometry-', '')))
        if (pluvioMapIds.length) {
            Promise.all(pluvioMapIds.map(id => StationAction.promiseMapSituationStats(STATION_TYPE_NAME.pluviometry, id))).then(jsonTab => setMapSituationStats(jsonTab.filter(t => t.length)))
        }
    }, [mapSituationWidgetObj])

    const stationsForTable = useMemo(() => {
        const bookmarksStations = bookmarks.map(code => pluviometers.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.PLUVIOMETRY && pluviometers.some(s2 => s.id === s2.id)).map(s => ({ ...s, isMonitored: true }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, [pluviometers, bookmarks, monitoredStations])

    const monitoredMaterielsFiltered = useMemo(() => monitoredMateriels.filter(m => m.typeSite === pluvioStationType), [monitoredMateriels])

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length) {
            return null
        }
        const tableData = stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const result = new DtoSimpleStation(station)
            return {
                ...result,
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                comment: station.eventComment,
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
            }
        })
        const mapSituationPies = mapSituationStats.map(tab => (
            <Grid key={tab} size={4}>
                <MapSituationPie stats={tab} st={STATION_TYPE_NAME.pluviometry} />
            </Grid>
        ))
        const mapSituationCard = mapSituationPies.length ? (
            <Grid container justifyContent='center' alignItems='center' spacing={3}>
                {mapSituationPies}
            </Grid>
        ) : undefined
        return (
            <>
                {mapSituationCard}
                <NewTable
                    headers={['nullValue', 'code', 'city', 'name', 'comment']}
                    rows={tableData}
                    onClickRow={s => dispatch(push(`/station/pluviometry/${s.id}`))}
                />
            </>
        )
    }, [citiesIndex, stationsForTable, mapSituationStats, dispatch])

    if (dataLoaded) {
        if (!(componentHasHabilitations(H_PLUVIO_MODULE) && (stationsForTable.length > 0 || mapSituationStats.length > 0 || monitoredMaterielsFiltered.length > 0))) {
            return null
        }
        return (
            <Card>
                <CardTitle
                    title={i18n[STATION_TYPE_NAME.pluviometry]}
                    icon={<InsertChartIcon fontSize='large' />}
                    color={darkBlue}
                    onClick={() => dispatch(push(`/${STATION_TYPE_NAME.pluviometry}`))}
                />
                {bookmarksStationPanel}
                {monitoredMaterielsFiltered.length > 0 && (
                    <HabilitationRequired habilitation={H_MAT_MODULE}>
                        <MonitoredMaterielPortletPanel noHeader={true} stationType={pluvioStationType} />
                    </HabilitationRequired>
                )}
            </Card>
        )
    }
    return (
        <Card>
            <CardContent>
                <Grid container justifyContent='center' alignItems='center' direction='column' sx={{ pt: 2.5 }}>
                    <img src={loading} />
                    <Grid size={3}>
                        <ProgressBar withMessage message={i18n.pluviometryCardInProgress} progress={progressBar} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default PluviometryCard